// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Props:
 *  * navigationOutSide: To add arrows outside swiper container, to fix overflow issue.
 *  * xController: To create custom controller.
 *  * extendedStyles: to append style to the component
 */

/* eslint-disable spire/fenced-imports */
import SwiperCore, { A11y, Autoplay, Controller, Navigation, Pagination, Scrollbar } from "swiper";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";
/* eslint-enable spire/fenced-imports */

import ArrowNavigation from "@guestsupply/App/Components/XngCarousel/ArrowNavigation";
import { xSwiperStyle } from "@guestsupply/App/Components/XngCarousel/styles";
import { color } from "@guestsupply/XDL/Utilities";
import { mergeWithCss } from "@guestsupply/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import pick from "lodash/pick";
import React, { FC, isValidElement, useEffect, useState } from "react";
import { css } from "styled-components";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay]);

export type Props = SwiperProps & {
    extendedStyles?: SwiperStyle;
};

export interface SwiperStyle {
    container?: InjectableCss;
}

// project style
const swiperStyle: SwiperStyle = {
    container: {
        css: css`
            ${xSwiperStyle}
            &.with-padding {
                .swiper {
                    margin: -15px;
                    box-sizing: border-box;
                    .swiper-wrapper {
                        padding: 15px;
                        box-sizing: border-box;
                    }
                }
            }

            .xng-pagination-container {
                &:not(.visible) {
                    display: none;
                }
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-top: 30px;
                gap: 12px;
                & > div,
                & > button {
                    margin: 0;
                }
            }

            .xng-pagination-container > .swiper-scrollbar.swiper-scrollbar-horizontal {
                height: 8px;
                width: calc(
                    100% - 50px
                ); // -50px to stop the bar from flowing out of the scroll bar container on last item
                position: relative;
                top: unset;
                right: unset;
                bottom: unset;
                left: unset;
                transform: initial;
                display: flex;
                align-items: center;
                background: ${color("common.ui2")};
                border-radius: 4px;
                padding: 2px;
                & > .swiper-scrollbar-drag {
                    background: ${color("common.ui4")};
                    border-radius: 4px;
                    height: 4px;
                }
            }

            .swiper-horizontal > .xng-pagination-container {
                height: 18px;
                display: flex !important;
            }

            .xng-pagination-container {
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-top: 20px;
            }

            .swiper-pagination {
                position: relative;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-wrap: wrap;
                &.swiper-pagination-lock {
                    display: none;
                }
                .swiper-pagination-bullet {
                    width: 10px;
                    height: 10px;
                    margin: 8px 4px;
                    background: ${color("common.ui1")};
                    border: 1px solid ${color("common.ui4")};
                    opacity: 1;
                    &:hover:not(.swiper-pagination-bullet-active) {
                        background: ${color("common.ui8")};
                    }
                    &.swiper-pagination-bullet-active {
                        width: 10px;
                        height: 10px;
                        background: ${color("common.ui9")};
                    }
                }
            }
        `,
    },
};

const XngCarousel: FC<Props> = props => {
    const { children, className, extendedStyles, ...swiperProps } = props;
    const [isSwiperCssImported, setIsSwiperCssImported] = useState(false);
    const [styles] = useState(() => mergeWithCss(swiperStyle, extendedStyles));

    useEffect(() => {
        const getStyle = async () => {
            /* eslint-disable global-require */
            await require("swiper/swiper-bundle.min.css");
            /* eslint-enable global-require */
        };
        getStyle().then(_ => setIsSwiperCssImported(true));
    }, []);

    if (!isSwiperCssImported) {
        return null;
    }

    let paginationOptions;
    let scrollbarOptions;

    if (swiperProps.pagination) {
        paginationOptions =
            typeof swiperProps.pagination === "boolean"
                ? { el: ".swiper-pagination", clickable: true }
                : swiperProps.pagination;
    }

    if (swiperProps.scrollbar) {
        scrollbarOptions =
            typeof swiperProps.scrollbar === "boolean"
                ? { draggable: true, el: ".swiper-scrollbar", dragSize: 50 }
                : swiperProps.scrollbar;
    }

    return (
        <StyledWrapper {...styles.container} className={`carousel-main-wrapper ${className || ""}`}>
            {/* Add observer/observeParents in-order to re-init in case of content change  */}
            <Swiper
                {...swiperProps}
                pagination={paginationOptions}
                scrollbar={scrollbarOptions}
                observeParents
                observer
            >
                {React.Children.map(children, child => {
                    // for null case
                    if (!isValidElement(child)) {
                        return;
                    }

                    const { props } = child;
                    const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                    const childrenProps = omitMultiple(props, [...swiperSlidePropsKeys]);
                    const swiperSlideProps = pick(props, swiperSlidePropsKeys);

                    return (
                        <SwiperSlide {...swiperSlideProps}>
                            {React.createElement(child.type, childrenProps)}
                        </SwiperSlide>
                    );
                })}

                {(swiperProps.pagination || swiperProps.navigation || swiperProps.scrollbar) && (
                    <StyledWrapper className="xng-pagination-container">
                        {swiperProps.navigation && <ArrowNavigation arrowType="prev" showOnMobile={true} />}
                        {swiperProps.pagination && <div className="swiper-pagination" />}
                        {swiperProps.scrollbar && <div className="swiper-scrollbar" />}
                        {swiperProps.navigation && <ArrowNavigation arrowType="next" showOnMobile={true} />}
                    </StyledWrapper>
                )}
            </Swiper>
        </StyledWrapper>
    );
};

XngCarousel.defaultProps = {
    slidesPerView: "auto",
    navigation: true,
    simulateTouch: false,
    spaceBetween: 20,
    pagination: false,
    scrollbar: false,
    watchOverflow: true,
    updateOnWindowResize: true,
    centerInsufficientSlides: true,
    draggable: true,
    freeMode: false,
};

export default XngCarousel;
